/**
 * useGamification — preference hook for Orbit's planet/galaxy
 * gamification layer (#326).
 *
 * When ON (default): rings on Today represent active planet progress,
 * the /galaxy view is visible in the sidebar, and earning a planet
 * triggers a Nova celebration.
 *
 * When OFF: rings show generic stats (bookings/revenue/edits) like
 * the legacy dashboard, /galaxy disappears from nav, no celebrations.
 * For studios who want plain business software with no game layer.
 *
 * Stored in localStorage under `orbit.gamification` so the choice
 * survives reloads and is also readable from the iOS app via the
 * same AppStorage key (parity with useNewShell).
 *
 * Returns `[isOn, setIsOn]` so any component can both read AND
 * toggle the preference without prop-drilling.
 */
import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'orbit.gamification';

/** Read the current preference synchronously. Defaults to ON
 *  because the gamified planet system is the new headline DNA;
 *  studios have to actively opt out. */
function read(): boolean {
  if (typeof window === 'undefined') return true;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    // Treat unset as ON. Only an explicit "0" disables.
    return raw !== '0';
  } catch {
    return true;
  }
}

export function useGamification(): [boolean, (next: boolean) => void] {
  const [value, setValue] = useState<boolean>(read);

  useEffect(() => {
    // Cross-tab + cross-subscriber sync — `storage` fires on OTHER
    // tabs natively. We also dispatch from set() so subscribers in
    // the SAME tab re-render without a refresh.
    const handler = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return;
      setValue(e.newValue !== '0');
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  const set = useCallback((next: boolean) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, next ? '1' : '0');
      window.dispatchEvent(
        new StorageEvent('storage', {
          key: STORAGE_KEY,
          newValue: next ? '1' : '0',
        }),
      );
    } catch {
      /* private mode — set in-memory state so the toggle still works
         for the session even if it can't persist */
    }
    setValue(next);
  }, []);

  return [value, set];
}
